import { Attribute, Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { BankAccount, ContactInformation } from '../../../../models/masters/contact-information.interface';
import { CONTACT_ACTIONS, ContactActions, ContactService } from '../../../../services/masters/contact.service';
import { ContactInformationComponent } from '../contact-info/contact-information.component';
import { FinanceComplianceComponent } from '../finance-compliance/finance-compliance.component';
import { VerticalTabsComponent } from '../../../vertical-tabs/vertical-tabs.component';
import { InfoCardBannerComponent } from '../../../info-card-banner/info-card-banner.component';
import { SharedModule } from '../../../../modules/shared/shared.module';
import { SvgIconComponent } from '../../../svg-icon/svg-icon.component';
import { RightPanelComponent } from '../../../right-panel/right-panel.component';
import { BankAccountFormComponent } from '../bank-account-form/bank-account-form.component';

import vendorContactConfig from "../config/vendor-contact-config.json"
import { DynamicForm } from '../../../../models/dynamic-form';
import { ToasterService } from '../../../../services/toaster.service';
import { NoDataComponent } from '../../../no-data/no-data.component';
import { FileEntity } from '../../../../models/common-model.interface';

@Component({
  selector: 'app-vendor-contact',
  standalone: true,
  imports: [VerticalTabsComponent, FinanceComplianceComponent, ContactInformationComponent, InfoCardBannerComponent, SharedModule, SvgIconComponent, NoDataComponent],
  templateUrl: './vendor-contact.component.html',
  styleUrl: './vendor-contact.component.scss'
})
export class VendorContactComponent {

  @Input() contactInformation: ContactInformation | null = null;
  @Input() action!: ContactActions;

  @Output() onValueChange = new EventEmitter<{controlName: string, result: any}>();

  contactFormConfig!: DynamicForm;
  bankAccountList: BankAccount[] = [];

  private sectionMap: Map<string, any> = new Map();

  selectedTab: string = "contactSection";

  tabList: any[] = [
    {
      id: "contactSection",
      label: "Contact Details",
      subtitle: "Address and contact person information",
      icon: "job_order.svg"
    },
    {
      id: "tdsSection",
      label: "TDS, MSME & LUT",
      subtitle: "Tax related information",
      icon: "package.svg"
    },
    {
      id: "accountSection",
      label: "Bank Details",
      subtitle: "Bank accounts associated to the location",
      icon: "package.svg"
    }
  ];

  @ViewChild("contactSection") contactSection: ElementRef | undefined;
  @ViewChild("tdsSection") tdsSection: ElementRef | undefined;
  @ViewChild("accountSection") accountSection: ElementRef | undefined;

  @ViewChild(ContactInformationComponent) contactInfoComponent!: ContactInformationComponent;
  @ViewChild(FinanceComplianceComponent) financeComplianceComponent!: FinanceComplianceComponent;

  constructor(private contactService: ContactService,
    private viewContainerRef: ViewContainerRef,
    private toasterService: ToasterService
  ) {
      this.contactFormConfig = vendorContactConfig as DynamicForm;
  }

  closeModal!: (params?: any) => void;

  close(params?: any) {
    if (this.closeModal) {
      this.closeModal(params);
    }
  }

  ngOnInit() {
    this.patchData();
  }

  ngAfterViewInit() {
    if (this.contactSection)
      this.sectionMap.set("contactSection", this.contactSection.nativeElement);
    if (this.tdsSection)
      this.sectionMap.set("tdsSection",this.tdsSection.nativeElement);
    if (this.accountSection)
      this.sectionMap.set("accountSection",this.accountSection.nativeElement);
  }

  selectTab(sectionId: string) {
    this.selectedTab = sectionId;
    const section = this.sectionMap.get(sectionId);
    const container = document.querySelector(".full_page_container");
    
    if (section && container) {
      const sectionTop = section.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollTo({top: sectionTop + container.scrollTop, behavior: "smooth"});
    }
  }

  get currentTabIndex(): number {
    return this.tabList.findIndex(tab => tab.id === this.selectedTab);
  }
  
  get isLastTab(): boolean {
    return this.currentTabIndex === this.tabList.length - 1;
  }

  goToNextTab() {
    const nextIndex = this.currentTabIndex + 1;
    if (nextIndex < this.tabList.length) {
      this.selectTab(this.tabList[nextIndex].id);
    }
  }

  private patchData(): void {
    if (!this.contactInformation || this.action === CONTACT_ACTIONS.CLONE) return;
    this.bankAccountList = this.contactInformation.bankAccounts ?? [];
  }

  submit() {
    const contactForm = this.contactInfoComponent.contactForm;
    const financeForm = this.financeComplianceComponent;
  
    this.contactInfoComponent.contactForm.markAllAsTouched();
    this.financeComplianceComponent.markAllAsTouched();
  
    if (!contactForm.valid || !financeForm.isFormValid()) {
      this.toasterService.error("Please fill in all required information");
      return;
    }
  
    const contactInfo = this.contactService.createContactFromForm(contactForm);
    contactInfo.bankAccounts = this.bankAccountList;
  
    const [tdsForm, msmeForm, lutForm] = [
      financeForm.getTdsForm(),
      financeForm.getMsmeForm(),
      financeForm.getLutForm()
    ];
  
    const tdsInfo = this.contactService.buildTdsInfo(tdsForm);
    const msmeInfo = this.contactService.buildMsmeInfo(msmeForm);
    const lutInfo = this.contactService.buildLutInfo(lutForm);
  
    if (tdsInfo) {
      tdsInfo.file = this.getTdsAttachment();
      tdsInfo.uploadedFile = this.contactInformation?.tdsMetadata?.uploadedFile;
      tdsInfo.fileId = this.contactInformation?.tdsMetadata?.fileId;
    }
  
    if (msmeInfo) {
      msmeInfo.file = this.getMsmeAttachment();
      msmeInfo.uploadedFile = this.contactInformation?.msmeInfo?.uploadedFile;
      msmeInfo.fileId = this.contactInformation?.msmeInfo?.fileId;
    }
  
    if (lutInfo) {
      lutInfo.file = this.getLutAttachment();
      lutInfo.uploadedFile = this.contactInformation?.lutInfo?.uploadedFile;
      lutInfo.fileId = this.contactInformation?.lutInfo?.fileId;
    }
  
    Object.assign(contactInfo, { tdsMetadata: tdsInfo, msmeInfo, lutInfo });
  
    this.closeModal(contactInfo);
  }
  

  handleBankAccountAction(index: number, action: string) {
    if (action === 'edit') {
      this.openAttributePanel(CONTACT_ACTIONS.EDIT, index);
    } else if (action === 'view') {
      this.openAttributePanel(CONTACT_ACTIONS.VIEW, index);
    }
  }

  addAccount() {
    this.openAttributePanel(CONTACT_ACTIONS.ADD);
  }

  getFormattedBankSummary(i: number): string[] {
    return Array.of(this.contactService.getFormattedBankSummary(this.bankAccountList[i]));
  }

  getBankAccountBadges(i: number): { label: string; class: string }[] {
    return this.contactService.getBankAccountBadges(this.bankAccountList[i]);
  }

  getTdsAttachment(): FileEntity {
    return this.financeComplianceComponent.tdsAttachment?.[0];
  }
  
  getMsmeAttachment(): FileEntity {
    return this.financeComplianceComponent.msmeAttachment?.[0];
  }

  getLutAttachment(): FileEntity {
    return this.financeComplianceComponent.lutAttachment?.[0];
  }

  openAttributePanel(action: ContactActions, index: number = 0) {
    
    const rightPanelComponentRef = this.viewContainerRef.createComponent(RightPanelComponent);
    rightPanelComponentRef.instance.title = action === CONTACT_ACTIONS.EDIT ? "Edit Account" : "Add Account";
    rightPanelComponentRef.instance.showActionBtns = action !== CONTACT_ACTIONS.VIEW;
    
    const manageAttributeRef = rightPanelComponentRef.instance.loadComponent(BankAccountFormComponent);
    manageAttributeRef.setInput("bankAccount", index !== undefined ? this.bankAccountList[index] : null);
    manageAttributeRef.setInput("viewMode", action === CONTACT_ACTIONS.VIEW);
    
    rightPanelComponentRef.instance.primaryActionBtnEmitter.subscribe((data) => {
      manageAttributeRef.instance.saveForm();
    })

    manageAttributeRef.instance.onSave.subscribe((savedAccount: BankAccount) => {
      if (savedAccount) {
        if (action === CONTACT_ACTIONS.ADD) {
          this.bankAccountList.push(savedAccount);
          index = this.bankAccountList.length - 1;
        } else if (action === CONTACT_ACTIONS.EDIT && index !== undefined) {
          this.bankAccountList[index] = savedAccount;
        }
        this.onBankStatusChange(savedAccount.active, index);
      }
      rightPanelComponentRef.destroy();
    });

    rightPanelComponentRef.instance.close.subscribe(() => {rightPanelComponentRef.destroy()})
  }

  onBankStatusChange(state: boolean, i: number) {
    if (state) {
      this.bankAccountList.forEach((contact, index) => {
        contact.active = index === i ? state : false;
      });
    }
  }
}
